import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DxFormComponent } from 'devextreme-angular';
import { ToastrService } from 'ngx-toastr';
import { filter, forkJoin, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { LoadingService } from 'src/app/services/loading.service';
import { FileUtilService } from 'src/app/services/file-util.service';
import { TranslateService } from '@ngx-translate/core';
import { ComplianceCategoryCreationAndUpdateModel } from 'src/app/models/compliance/compliance-category-creation-and-update.model';
import { ComplianceCategoryFullModel } from 'src/app/models/compliance/compliance-category-full.model';
import { DxSelectBoxTypes } from 'devextreme-angular/ui/select-box';
import { ComplianceRenewalFrequencyModel } from 'src/app/models/compliance/compliance-renewal-frequency.model';
import { LearningPathModel } from 'src/app/models/learning-paths/learning-path.model';
import { OrganisationUnitModel } from 'src/app/models/organisation-unit/organisation-unit.model';
import { TrainingGroupModel } from 'src/app/models/training-group.model';
import { OrganisationUnitService } from 'src/app/services/organisation-unit.service';
import { PersonService } from 'src/app/services/person.service';
import { TrainingCategoryService } from 'src/app/services/training-categories/training-category.service';
import { TrainingGroupService } from 'src/app/services/training-group.service';
import { TrainingHelperService } from 'src/app/services/training-helper.service';
import { LearningPathService } from 'src/app/services/learning-path.service';
import { SelectOption } from 'src/app/models/select-option.model';
import { ComplianceCategoryService } from 'src/app/services/compliance-category.service';
import { ComplianceAssociatedCourseModel } from 'src/app/models/compliance/compliance-associated-course.model';
import { ComplianceCourseIddProgressModel } from 'src/app/models/compliance/compliance-course-idd-progress.model';
import { ComplianceLocationIddProgressModel } from 'src/app/models/compliance/compliance-location-idd-progress.model';
import { TrainingCategoryModel } from 'src/app/models/training-category/training-category.model';
import { TextTemplateTypeModel } from 'src/app/models/text-templates/text-template-type.model';
import { UserContextService } from 'src/app/services/user-context.service';
import { isEqual } from 'lodash-es';
import { ConfirmationDialogData } from 'src/app/components/shared/confirmation-dialog/confirmation-dialog.component';
import { PageStateFlags } from 'src/app/constants/page-state.constants';
import { PageStateService } from 'src/app/services/page-state.service';
import { EventVisibilityModeModel } from 'src/app/models/event-visibility-mode.model';

@Component({
  selector: 'app-edit-manage-compliance',
  standalone: false,
  templateUrl: './edit-manage-compliance.component.html',
  styleUrl: './edit-manage-compliance.component.scss',
})
export class EditManageComplianceComponent implements OnInit {
  @ViewChild('descriptionForm', { static: true }) descriptionForm!: DxFormComponent;
  @ViewChild('settingsForm', { static: true }) settingsForm!: DxFormComponent;

  renewalFrequencyOptions!: DxSelectBoxTypes.Properties;
  reminderDaysUntilSupervisorOptions!: DxSelectBoxTypes.Properties;
  escalationReminderDaysOptions!: DxSelectBoxTypes.Properties;
  
  private readonly remindersDaysUntilSupervisorValues = [0, 2, 3, 4, 5, 6];
  private readonly escalationReminderDaysValues = [3, 4, 5, 6];

  formData: ComplianceCategoryCreationAndUpdateModel = {
    name: '',
    description: '',
    descriptionExtended: '',
    image: null,

    renewalFrequency: null,
    reminderDays: 0,
    remindersUntilSupervisorInfo: 0,
    escalationReminder: false,
    escalationReminderNumber: null,
    escalationReminderDays: null,
    reassignBeforeExpiry: true,

    superAdminPersonIds: [],
    organisationUnitLocationIds: [],
    trainingGroupIds: [],
    trainingCategoryIds: [],
    learningPathIds: [],
  };

  visibilityMode: EventVisibilityModeModel = 
    EventVisibilityModeModel.Everyone;

  initialFormData: any;

  organizationUnits!: OrganisationUnitModel[];
  trainingGroups!: TrainingGroupModel[];
  trainingCategories!: TrainingCategoryModel[];
  learningPaths!: LearningPathModel[];
  persons!: SelectOption[];

  associatedCourses: ComplianceAssociatedCourseModel[] | null = null;
  complianceIddProgress: ComplianceCourseIddProgressModel | null = null;
  complianceLocationIddProgress: ComplianceLocationIddProgressModel[] | null = null;

  selectedOrganizationUnitIds: any[] = [];

  currentComplianceCategory: ComplianceCategoryFullModel | null = null;
  editingMode = false;

  unsavedChangesModalOptions: ConfirmationDialogData | null = null;
  showUnsavedChangesModal = false;

  get isNeverRenewalFrequency(): boolean {
    return this.formData.renewalFrequency === ComplianceRenewalFrequencyModel.Never;
  }

  personCount: number = 0;

  get isVisibleToEveryone() {
    return this.isAllOrganizationUnitsSelected &&
      this.formData.trainingGroupIds.length == 0
  }

  get isAllOrganizationUnitsSelected() {
    const selectedOrgUnitCount = 
      this.selectedOrganizationUnitIds.length;
      
    return selectedOrgUnitCount == 0
      || selectedOrgUnitCount == this.organizationUnits.length;
  }

  
  descriptionTextTemplateType: TextTemplateTypeModel =
    TextTemplateTypeModel.ExtendedDescription;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private loadingService: LoadingService,
    private toastrService: ToastrService,
    private fileUtilService: FileUtilService,
    private translateService: TranslateService,
    private organisationUnitService: OrganisationUnitService,
    private personService: PersonService,
    private trainingGroupService: TrainingGroupService,
    private trainingCategoryService: TrainingCategoryService,
    readonly trainingHelperService: TrainingHelperService,
    private learningPathService: LearningPathService,
    private complianceCategoryService: ComplianceCategoryService,
    public userContext: UserContextService,
    private pageStateService: PageStateService
  ) {}

  ngOnInit(): void {
    this.setComplianceDropdownOptions();
    
    this.activatedRoute.params
      .pipe(
        map(params => params['id']),
        filter(id => !!id),
        tap(id => (this.editingMode = id !== 'new'))
      )
      .subscribe(id => {
        this.loadData(id);
      });
  }

  private loadData(id: string): void {
    const formData$ = this.loadingService.load(
      forkJoin({
        trainingGroups: this.trainingGroupService.getAllTrainingGroups(),
        trainingCategories: this.trainingCategoryService.getAllTrainingCategories(),
        organizationUnits: this.organisationUnitService.getAllOrganisationUnits(),
        learningPaths: this.learningPathService.getAllLearningPaths(),
        persons: this.personService.getPersonsWithoutBrokers(),
        personCount: this.personService.getPersonCount(),
      })
    ).pipe(shareReplay(1));

    const complianceData$ = this.editingMode ? this.loadComplianceData(id) : of(null);

    this.loadingService.load(
      forkJoin({
        formData: formData$,
        complianceData: complianceData$,
      })
    )
    .subscribe(({ formData, complianceData }) => {
      this.trainingGroups = formData.trainingGroups;
      this.trainingCategories = formData.trainingCategories;
      this.organizationUnits = formData.organizationUnits;
      this.learningPaths = formData.learningPaths;
      this.personCount = formData.personCount;
      this.persons = formData.persons.map(person => ({
        id: person.id,
        name: `${person.firstName} ${person.lastName}`,
      }));
      
      if (complianceData) {
        this.applyComplianceData(complianceData);
      }

      this.setEmployeeVisibilityMode();
    });
  }

  private loadComplianceData(id: string) {
    return this.complianceCategoryService.getComplianceCategoryFull(id).pipe(
      switchMap(categoryFull => {
        const associatedCourses$ = this.complianceCategoryService.getComplianceAssociatedCourses(id);

        return forkJoin({
          complianceCategory: of(categoryFull),
          associatedCourses: associatedCourses$,
          complianceIddProgress: categoryFull.assignedCourse
            ? this.complianceCategoryService.getComplianceCourseIddProgress(id)
            : of(null),
          complianceLocationIddProgress: categoryFull.assignedCourse
            ? this.complianceCategoryService.getComplianceLocationIddProgress(id)
            : of(null),
        });
      })
    );
  }

  private applyComplianceData(complianceData: any) {
    this.associatedCourses = complianceData.associatedCourses;
    this.complianceIddProgress = complianceData.complianceIddProgress;
    this.currentComplianceCategory = complianceData.complianceCategory;
    this.complianceLocationIddProgress = complianceData.complianceLocationIddProgress;

    const imageUrl = this.currentComplianceCategory!.complianceCategory.imageFile?.fileUrl;

    if(!imageUrl) {
      this.patchFormData(this.currentComplianceCategory!);
      return;
    }

    if (imageUrl) {
      this.loadingService.load(
        this.fileUtilService.avatarUrlToBase64(imageUrl)
      ).subscribe((base64) => {
        const mimeType = this.fileUtilService.getImageMimeType(imageUrl);
        this.formData.image = `data:${mimeType};base64,${base64}`;
        this.patchFormData(this.currentComplianceCategory!);
      });
    }
  }

  private setEmployeeVisibilityMode() {
    this.visibilityMode = this.isVisibleToEveryone
      ? EventVisibilityModeModel.Everyone
      : EventVisibilityModeModel.Restricted;
  }

  setComplianceDropdownOptions() {
    const defaultSelectOptions = {
      stylingMode: 'outlined',
      displayExpr: 'name',
      valueExpr: 'id'
    } as DxSelectBoxTypes.Properties;

    this.renewalFrequencyOptions = {
      ...defaultSelectOptions,
      showClearButton: true,
      dataSource: [
        {
          id: ComplianceRenewalFrequencyModel.ThreeMonths,
          name: this.translateService.instant('COMPLIANCE.RENEWAL.3-MONTHS')
        },
        {
          id: ComplianceRenewalFrequencyModel.SixMonths,
          name: this.translateService.instant('COMPLIANCE.RENEWAL.6-MONTHS')
        },
        {
          id: ComplianceRenewalFrequencyModel.OneYear,
          name: this.translateService.instant('COMPLIANCE.RENEWAL.1-YEAR')
        },
        {
          id: ComplianceRenewalFrequencyModel.TwoYears,
          name: this.translateService.instant('COMPLIANCE.RENEWAL.2-YEARS')
        },
        {
          id: ComplianceRenewalFrequencyModel.ThreeYears,
          name: this.translateService.instant('COMPLIANCE.RENEWAL.3-YEARS')
        },
        {
          id: ComplianceRenewalFrequencyModel.Never,
          name: this.translateService.instant('COMPLIANCE.RENEWAL.NEVER')
        }
      ],
      placeholder: '',
      onValueChanged: (e: any) => this.onRenewalFrequencyChanged(e)
    };

    this.reminderDaysUntilSupervisorOptions = {
      ...defaultSelectOptions,
      showClearButton: true,
      dataSource: [
        ...this.remindersDaysUntilSupervisorValues.map(value => ({
          id: value,
          name: value === 0 
            ? this.translateService.instant('COMMON.EMPTY') 
            : value.toString()
        }))
      ],
      placeholder: ''
    };

    this.escalationReminderDaysOptions = {
      ...defaultSelectOptions,
      showClearButton: true,
      dataSource: [
        ...this.escalationReminderDaysValues.map(v => ({
          id: v,
          name: v.toString()
        }))
      ],
      placeholder: ''
    };
  }
  
  setTreeViewValues() {
    this.trainingGroups.forEach((trainingGroup) => {
      if (this.formData.trainingGroupIds.indexOf(trainingGroup.id) > -1) {
        trainingGroup.selected = true;
      }
    });

    this.trainingCategories.forEach((trainingCategory) => {
      if (
        this.formData.trainingCategoryIds.indexOf(trainingCategory.id) > -1
      ) {
        trainingCategory.selected = true;
      }
    });

    this.organizationUnits.forEach((unit) => {
      if (this.selectedOrganizationUnitIds.indexOf(unit.id) > -1) {
        unit.selected = true;
      }
    });

    this.learningPaths.forEach((learningPath) => {
      if (this.formData.learningPathIds.indexOf(learningPath.id) > -1) {
        learningPath.selected = true;
      }
    });

    this.persons.forEach((person) => {
      if (this.formData.superAdminPersonIds.indexOf(person.id) > -1) {
        person.selected = true;
      }
    });
  }

  hasUnsavedChanges(): boolean {
    const current = structuredClone(this.formData);
    const original = structuredClone(this.initialFormData);

    this.normalizeForm(current);
    this.normalizeForm(original);

    return !isEqual(current, original);
  }

  private normalizeForm(form: any): void {
    if (!form) return;

    if (form.descriptionExtended) {
      form.descriptionExtended = this.normalizeHtml(form.descriptionExtended);
    }
  }

  private normalizeHtml(html: string): string {
    return html
      ?.replace(/\sstyle="[^"]*"/g, '') // remove inline styles
      ?.replace(/\s+/g, ' ')           // normalize whitespace
      ?.trim();
  }

  onRenewalFrequencyChanged(e: any) {
    const selectedValue = e.value;

    if(selectedValue === ComplianceRenewalFrequencyModel.Never) {
      this.formData.reassignBeforeExpiry = false;
      return;
    } 
  }

  navigateBack(): void {
    this.pageStateService.setFlag(
      PageStateFlags.RestoreComplianceCategoriesOverview
    );

    this.router.navigate(['manage-compliance']);
  }
  

  private unsavedChangesAction!: () => void;

  onCreateCourse(): void {
    this.handleUnsavedChanges(() => 
      this.newComplianceCourseNavigateHandler());
  }

  onCourseClick(id: string): void {
    this.handleUnsavedChanges(() => 
      this.complianceCourseNavigateHandler(id));
  }

  private handleUnsavedChanges(action: () => void): void {
    if (!this.hasUnsavedChanges()) {
      action();
      return;
    }

    this.unsavedChangesAction = action;
    this.openUnsavedChangesConfirmationModal();
  }

  onUnsavedChangesConfirmed(): void {
    this.unsavedChangesAction();
  }

  newComplianceCourseNavigateHandler(): void {
    if(!this.currentComplianceCategory) return;

    this.router.navigate([
      'manage-compliance',
      this.currentComplianceCategory.complianceCategory.id,
      'manage-elearning',
      'edit', 'new'
    ]);
  }

  complianceCourseNavigateHandler(id: string): void {
    if(!this.currentComplianceCategory) return;

    this.router.navigate([
      'manage-compliance', 
      this.currentComplianceCategory.complianceCategory.id,
      'manage-elearning',
      id
    ]);
  }


  openUnsavedChangesConfirmationModal() {
    this.unsavedChangesModalOptions = {
      title: this.translateService.instant(
        'COMPLIANCE.FORM.UNSAVED-CHANGES-DIALOG.TITLE'
      ),
      message: this.translateService.instant(
        'COMPLIANCE.FORM.UNSAVED-CHANGES-DIALOG.TEXT'
      ),
      submitButtonText: this.translateService.instant('COMMON.YES'),
      dismissButtonText: this.translateService.instant('COMMON.NO'),
      hideUndo: true
    };
    this.showUnsavedChangesModal = true;
  }

  submit(): void {
    const isFormValid = this.isFormValid();

    if (!isFormValid) {
       this.toastrService.error(
        this.translateService.instant('VALIDATIONS.VALUES-NOT-VALID')
      );
      return;
    }

    if(this.visibilityMode === EventVisibilityModeModel.Everyone) {
      this.selectedOrganizationUnitIds = [];
      this.formData.trainingGroupIds = [];
    }

    this.formData.organisationUnitLocationIds = [];

    if (!this.selectedOrganizationUnitIds.length) {
      this.organizationUnits.forEach((unit) => {
        this.formData.organisationUnitLocationIds.push(unit.id);
      });
    } else {
      this.selectedOrganizationUnitIds.forEach((id) => {
        this.formData.organisationUnitLocationIds.push(id);
      });
    }

    const model: ComplianceCategoryCreationAndUpdateModel = {
      ...this.formData,
      image: this.formData.image ? this.formData.image!.split(',')[1] : '',
    };

    if (this.editingMode && this.currentComplianceCategory) {
      this.loadingService
        .load(
          this.complianceCategoryService.updateComplianceCategory(
            this.currentComplianceCategory.complianceCategory.id,
            model
          )
        )
        .pipe(
          switchMap(() =>
            this.loadingService.load(
              this.loadComplianceData(this.currentComplianceCategory!.complianceCategory.id)
            )
          )
        )
        .subscribe((complianceData) => {
          this.applyComplianceData(complianceData)

          this.toastrService.success(
            this.translateService.instant(
              "COMPLIANCE.OVERVIEW.COMPLIANCE-CATEGORY-UPDATED-SUCCESSFULLY"
            )
          );
        });
    } else {
      const obs = this.complianceCategoryService.addComplianceCategory(model);
      this.loadingService.load(obs).subscribe((createdComplianceCategory) => {
        this.toastrService.success(
          this.translateService.instant(
            "COMPLIANCE.OVERVIEW.COMPLIANCE-CATEGORY-ADDED-SUCCESSFULLY"
          )
        );

        this.router.navigate([
          'manage-compliance',
          createdComplianceCategory.complianceCategory.id,
        ]);
      });
    }
  }

  isFormValid(): boolean {
    let isValidationFailed = false;

    const isDescriptionFormValid = this.descriptionForm.instance.validate();
    const isSettingsFormValid = this.settingsForm.instance.validate();

    if (!isDescriptionFormValid.isValid && !isValidationFailed) {
      // @ts-ignore
      isDescriptionFormValid.brokenRules[0].validator.focus();
      isValidationFailed = true;
    }

    if (!isSettingsFormValid.isValid && !isValidationFailed) {
      // @ts-ignore
      isSettingsFormValid.brokenRules[0].validator.focus();
      isValidationFailed = true;
    }

    return !isValidationFailed;
  }

  patchFormData(compliance: ComplianceCategoryFullModel) {
    this.selectedOrganizationUnitIds = compliance.organisationUnits.map(
      (unit) => unit.id
    );

    this.formData.name = compliance.complianceCategory.name;
    this.formData.description = compliance.complianceCategory.description;
    this.formData.descriptionExtended = compliance.complianceCategory.descriptionExtended;
    this.formData.image = compliance.complianceCategory.imageFile?.fileUrl ?? null;

    this.formData.renewalFrequency = compliance.complianceCategory.renewalFrequency ?? null;
    this.formData.reminderDays = compliance.complianceCategory.reminderDays ?? null;
    this.formData.remindersUntilSupervisorInfo = compliance.complianceCategory.remindersUntilSupervisorInfo ?? null;

    this.formData.escalationReminder = compliance.complianceCategory.escalationReminder;
    this.formData.escalationReminderNumber = compliance.complianceCategory.escalationReminderNumber ?? null;
    this.formData.escalationReminderDays = compliance.complianceCategory.escalationReminderDays ?? null;

    this.formData.reassignBeforeExpiry = compliance.complianceCategory.reassignBeforeExpiry;

    this.formData.superAdminPersonIds = compliance.superAdminPersons.map(x => x.id);
    this.formData.organisationUnitLocationIds = compliance.organisationUnits.map(x => x.id);
    this.formData.trainingGroupIds = compliance.trainingGroups.map(x => x.id);
    this.formData.trainingCategoryIds = compliance.trainingCategories.map(x => x.id);
    this.formData.learningPathIds = compliance.learningPaths.map(x => x.id);

    this.setTreeViewValues();
    this.initialFormData = structuredClone(this.formData);
  }

  onCategoryImageSelected(files: any) {
    const file = files[0];
    const reader = new FileReader();
    reader.onload = () => {
      this.formData!.image = reader!.result!.toString();
    };
    reader.readAsDataURL(file);
  }

  onLearningPathSelected(e: any) {
    if (e.node.selected) {
      this.formData.learningPathIds = [
        ...this.formData.learningPathIds,
        e.node.key,
      ];
    } else {
      this.formData.learningPathIds =
        this.formData.learningPathIds.filter((id) => id !== e.node.key);
    }
  }

  onSuperAdminPersonSelected(e: any) {
    if (e.node.selected) {
      this.formData.superAdminPersonIds = [
        ...this.formData.superAdminPersonIds,
        e.node.key,
      ];
    } else {
      this.formData.superAdminPersonIds = this.formData.superAdminPersonIds.filter(
        (id) => id !== e.node.key
      );
    }
  }

  onReminderDaysChanged(e: any) {
    const value = e.value;

    if (!value) {
      this.formData.escalationReminder = false;
      this.formData.escalationReminderNumber = null;
      this.formData.escalationReminderDays = null;
    }
  }

  onEscalationReminderChanged(e: any) {
    const value = e.value;

    if (!value) {
      this.formData.escalationReminderNumber = null;
      this.formData.escalationReminderDays = null;
    }
  }

  onTemplateContentChanged(content: string) {
    this.formData.descriptionExtended = content;
  }
}
